// D:\brickbucket_v3.1\Brickbucket_Frontend_V3.1\src\ConsumerApp\Screens\Home\Home.js

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, CenteredContainer } from '../../DesignSystem';
import { theme } from '../../../assets/theme/theme';
import Bucket from '../Bucket/Bucket';

// SVG Icons matching React Native exactly[cite: 8]
import ToteIcon from '../../../assets/icons/tote.svg';
import WhatsappLogo from '../../../assets/icons/WhatsApp.svg';
import ArrowRight from '../../../assets/icons/ArrowRight.svg';

// Shared API Hooks
import { useGetCategories } from '../../api/categories/category.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';
import {
  useGetMostSearchedProducts,
  useGetBucketSummary,
} from '../../api/products/product.hooks';
import { useGetOrdersByCustomer } from '../../api/orders/orders.hooks';

const IMAGE_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';
const BUSINESS_WHATSAPP_NUMBER = '918639065454';

// Popular Brands Data[cite: 8]
const POPULAR_BRANDS = [
  { id: '1', name: 'UltraTech', label: 'UltraTech\nCEMENT', color: '#FACC15', textColor: '#1C2833' },
  { id: '2', name: 'Dalmia', label: 'Dalmia\ncement', color: '#FFFFFF', textColor: '#0F4392' },
  { id: '3', name: 'Asian Paints', label: 'ap\nasianpaints', color: '#FFFFFF', textColor: '#E9001E' },
  { id: '4', name: 'Finolex', label: 'Finolex\nPIPES', color: '#FFFFFF', textColor: '#0F4392' },
  { id: '5', name: 'Supreme', label: 'Supreme\nPlastics', color: '#FFFFFF', textColor: '#E9001E' },
  { id: '6', name: 'MYK Arment', label: 'MYK\narment', color: '#FFFFFF', textColor: '#0F4392' },
];

function CategoryCardImage({ imageUri }) {
  const [hasError, setHasError] = useState(false);

  if (!imageUri || hasError) {
    return (
      <div style={styles.categoryFallbackBox}>
        <img src={ToteIcon} alt="Category fallback" style={styles.toteIcon28} />
      </div>
    );
  }

  return (
    <img
      src={imageUri}
      alt="Category"
      style={styles.categoryImg}
      onError={() => setHasError(true)}
    />
  );
}

function RecentItemImage({ imageUri }) {
  const [hasError, setHasError] = useState(false);

  if (!imageUri || hasError) {
    return (
      <div style={styles.recentImageFallback}>
        <img src={ToteIcon} alt="Product fallback" style={styles.toteIcon20} />
      </div>
    );
  }

  return (
    <img
      src={imageUri}
      alt="Product"
      style={styles.recentImage}
      onError={() => setHasError(true)}
    />
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isBucketOpen, setIsBucketOpen] = useState(false);

  // Authenticated customer retrieval
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const customerId = customer?._id;

  // 1. Live Queries
  const { data: rawAddress } = useGetDefaultDeliveryAddress(customerId);
  const { data: rawCategories, isLoading: isCategoriesLoading, isError: isCategoriesError } = useGetCategories();
  const { data: rawMostSearched, isLoading: isProductsLoading } = useGetMostSearchedProducts();
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: ordersData } = useGetOrdersByCustomer(customerId);
  console.log(rawMostSearched);

  // 2. Computed Values
  const selectedAddress = useMemo(() => {
    const addr = rawAddress?.data || rawAddress;
    if (!addr) return 'Select delivery address';
    return addr.address_line_1 || addr.complete_address || addr.city || 'My Site Address';
  }, [rawAddress]);

  const cartItemCount = bucketSummary?.buy_now_bucket?.items_count || 0;

  const categories = useMemo(() => {
    const list = Array.isArray(rawCategories) ? rawCategories : rawCategories?.data || [];
    return list.map((cat) => ({
      id: cat._id || cat.id,
      name: cat.product_category_name || cat.name || '',
      image: cat.product_category_image ? `${IMAGE_BASE_URL}${cat.product_category_image}` : null,
    }));
  }, [rawCategories]);
  //console.log(categories);

  const mostBoughtList = useMemo(() => {
    const list = Array.isArray(rawMostSearched) ? rawMostSearched : rawMostSearched?.data || [];
    return list;
  }, [rawMostSearched]);

   const activeLiveOrders = useMemo(() => {
    const all = ordersData?.orders || ordersData || [];
    if (!Array.isArray(all)) return [];
    const activeStatuses = ['Pending', 'Confirmed', 'Delivering'];
    return all.filter((o) => activeStatuses.includes(o.salesorder_status) || o.is_live_order);
  }, [ordersData]);

 const handleOpenWhatsAppRFQ = () => {
    const message = encodeURIComponent('Hi, I would like to request a quote (RFQ) for my material list.');
    window.open(`https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div style={styles.pageShell}>
      {/* Universal Desktop Navigation Bar */}
<Navbar
  searchQuery={searchQuery}
  onSearchChange={setSearchQuery}
/>

      <main style={styles.mainFeedShell}>
        <CenteredContainer>
          <div style={styles.feedColumn}>

            {/* LIVE ORDER BANNER */}
            {activeLiveOrders.length > 0 && (
              <div style={styles.liveOrderBanner}>
                <div style={styles.liveOrderLeft}>
                  <span style={styles.truckIconLive}>🚚</span>
                  <div>
                    <h4 style={styles.liveOrderHeading}>
                      Order {activeLiveOrders[0].salesorder_status === 'Delivering' ? 'Out for Delivery' : 'Confirmed'}
                    </h4>
                    <span style={styles.liveOrderSub}>
                      {activeLiveOrders[0].delivery_estimated_minutes
                        ? `Arriving in ${activeLiveOrders[0].delivery_estimated_minutes} mins`
                        : 'Your items are being prepped for dispatch'}
                    </span>
                  </div>
                </div>
                <button
                  style={styles.trackOrderBtn}
                  onClick={() => navigate(`/consumer/account/orders/${activeLiveOrders[0].order_id || activeLiveOrders[0]._id}`)}
                >
                  Track Live →
                </button>
              </div>
            )}

            {/* 1. TOP 3-CARD HERO DECK (Includes WhatsApp RFQ) */}
            <div style={styles.heroRow}>
              {/* Card 1: Fast Site Delivery */}
              <div style={styles.heroSlideCard}>
                <div style={styles.heroTextSection}>
                  <span style={styles.heroTagline}>Fast site delivery</span>
                  <h2 style={styles.heroHeading}>30–90 mins</h2>
                  <span style={styles.heroSubtext}>on eligible items</span>
                  <div
                    style={styles.heroLocationRow}
                    onClick={() => navigate('/consumer/account/delivery')}
                  >
                    <span style={styles.heroLocationLink}>📍 Check availability by location</span>
                  </div>
                </div>
                <div style={styles.truckIconPlaceholder}>
                  <span style={styles.truckEmoji}>🚚</span>
                </div>
              </div>

              {/* Card 2: Site Emergency Value-Prop */}
              <div style={styles.heroSlideAltCard}>
                <div style={styles.heroTextSection}>
                  <div style={styles.urgencyBadge}>
                    <span style={styles.urgencyBadgeText}>SITE EMERGENCY</span>
                  </div>
                  <h3 style={styles.heroHeadingAlt}>Running out of small items?</h3>
                  <p style={styles.heroSubtextAlt}>
                    Don't waste petrol and time visiting the market for a ₹125 fitting. We deliver in minutes.
                  </p>
                </div>
                <div style={styles.clockIconPlaceholder}>
                  <span style={styles.clockEmoji}>⚡</span>
                </div>
              </div>

              {/* Card 3: WhatsApp Bulk RFQ Card */}
              <div
                style={styles.heroWhatsAppCard}
                onClick={handleOpenWhatsAppRFQ}
              >
                <div style={styles.heroTextSection}>
                  <div style={styles.whatsappBadge}>
                    <img src={WhatsappLogo} alt="WhatsApp" style={styles.whatsappMiniSvg} />
                    <span style={styles.whatsappBadgeText}>BULK ESTIMATES</span>
                  </div>
                  <h3 style={styles.heroHeadingAlt}>Got bulk requirement?</h3>
                  <p style={styles.heroSubtextAlt}>
                    Share your material list on WhatsApp. Receive verified quotes directly on your phone.
                  </p>
                  <div style={styles.heroLocationRow}>
                    <span style={styles.heroLocationLinkGreen}>Request for Quote →</span>
                  </div>
                </div>
                <div style={styles.whatsappIconPlaceholder}>
                  <img src={WhatsappLogo} alt="WhatsApp" style={styles.whatsappHeroSvg} />
                </div>
              </div>
            </div>

            {/* 2. BROWSE BY CATEGORY */}
            <section style={styles.sectionContainer}>
              <div style={styles.sectionHeaderRow}>
                <h3 style={styles.sectionTitle}>Browse by Category</h3>
                {/* <div
                  style={styles.viewAllBtn}
                  onClick={() => navigate('/consumer/productcategories')}
                >
                  <span style={styles.viewAllText}>View All</span>
                  <img src={ArrowRight} alt="Arrow" style={styles.arrowIcon} />
                </div> */}
              </div>

           {isCategoriesLoading ? (
  <div style={styles.stateContainer}>Loading categories...</div>
) : isCategoriesError ? (
  <div style={styles.errorText}>Failed to load categories.</div>
) : (
  <div style={styles.categoriesGrid}>
  {categories.map((item) => (
    <div
      key={item.id}
      style={styles.categoryItemCard}
      className="category-card-hover"
      onClick={() =>
        navigate('/consumer/productslist', {
          state: { categoryId: item.id, categoryName: item.name },
        })
      }
    >
      <div style={styles.categoryImgBox}>
        <CategoryCardImage imageUri={item.image} />
      </div>
      <span style={styles.categoryItemName} title={item.name}>
        {item.name}
      </span>
    </div>
  ))}
</div>
)}
            </section>

            {/* 3. DISCOUNTS FOR YOU */}
            <section style={styles.sectionContainer}>
              <div style={styles.sectionHeaderRow}>
                <h3 style={styles.sectionTitle}>Discounts for You</h3>
                <div
                  style={styles.viewAllBtn}
                  onClick={() => navigate('/consumer/account/offers')}
                >
                  <span style={styles.viewAllText}>View All</span>
                  <img src={ArrowRight} alt="Arrow" style={styles.arrowIcon} />
                </div>
              </div>

              <div style={styles.discountsRow}>
                {/* Coupon Card */}
                <div
                  style={{ ...styles.discountCard, ...styles.couponCardBg }}
                  onClick={() => navigate('/consumer/account/offers')}
                >
                  <div style={styles.discountIconBadgeRed}>
                    <span style={styles.percentText}>%</span>
                  </div>
                  <div style={styles.discountCardTextGroup}>
                    <span style={styles.couponCodeTitle}>FIRSTFOUR</span>
                    <span style={styles.discountDesc}>₹100 off on your first 4 orders</span>
                    <span style={styles.discountMinOrder}>Min. order ₹999</span>
                  </div>
                  <img src={ArrowRight} alt="Arrow" style={styles.arrowIcon14} />
                </div>

                {/* Referral Card */}
                <div
                  style={{ ...styles.discountCard, ...styles.referralCardBg }}
                  onClick={() => navigate('/consumer/account/wallet')}
                >
                  <div style={styles.discountIconBadgeGreen}>
                    <span style={styles.referralIconText}>👥</span>
                  </div>
                  <div style={styles.discountCardTextGroup}>
                    <span style={styles.referralTitle}>Refer & Save</span>
                    <span style={styles.discountDesc}>You and your referral get ₹100 each</span>
                  </div>
                  <img src={ArrowRight} alt="Arrow" style={styles.arrowIcon14} />
                </div>
              </div>
            </section>

            {/* 4. FREQUENTLY BOUGHT BY BUYERS (No button, compact clickable card) */}
            {!isProductsLoading && mostBoughtList.length > 0 && (
              <section style={styles.sectionContainer}>
                <div style={styles.sectionHeaderRow}>
                  <h3 style={styles.sectionTitle}>Frequently bought by buyers</h3>
                </div>

                <div style={styles.productsGrid}>
                  {mostBoughtList.map((item) => {
                    const productId = item.product_id || item._id;
                    const name = item.product_name || item.name || 'Unknown Product';
                    const imgPath = item.product_image || item.product_image_url || item.image;
                    const buyNowPrice =
                      item?.buy_now?.unit_price ||
                      item?.selling_price ||
                      item?.unit_price ||
                      item?.price;
                    const mrp = item?.mrp;

                    return (
                      <div
                        key={productId}
                        style={styles.productCard}
                        onClick={() =>
                          navigate('/consumer/productdetails', { state: { productId } })
                        }
                      >
                        <RecentItemImage
                          imageUri={imgPath ? `${IMAGE_BASE_URL}${imgPath}` : null}
                        />

                        <div style={styles.productCardBody}>
                          <p style={styles.productName} title={name}>
                            {name}
                          </p>

                          <div style={styles.priceRow}>
                            <span style={styles.productPrice}>₹{buyNowPrice}</span>
                            {mrp && Number(mrp) > Number(buyNowPrice) && (
                              <span style={styles.productMrp}>₹{mrp}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* 5. OUR TRUSTED BRANDS */}
            <section style={styles.sectionContainer}>
              <div style={styles.sectionHeaderRow}>
                <h3 style={styles.sectionTitle}>Our Trusted Brands</h3>
              </div>

              <div style={styles.brandsGrid}>
                {POPULAR_BRANDS.map((brand) => (
                  <div
                    key={brand.id}
                    style={styles.brandLogoCard}
                    onClick={() =>
                      navigate('/consumer/productslist', { state: { brand: brand.name } })
                    }
                  >
                    <div
                      style={{
                        ...styles.brandInnerBox,
                        backgroundColor: brand.color,
                        color: brand.textColor,
                      }}
                    >
                      <span style={styles.brandLogoText}>{brand.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 6. VIDEO TESTIMONIAL BANNER */}
            <div
              style={styles.testimonialBanner}
              onClick={() => window.open('https://brickbucket.in', '_blank')}
            >
              <div style={styles.playIconCircle}>
                <span style={styles.playIconText}>▶</span>
              </div>
              <div style={styles.testimonialTextCol}>
                <h4 style={styles.testimonialTitle}>Why Buyers Use Brickbucket</h4>
                <p style={styles.testimonialSubtext}>
                  Real stories from site supervisors and contractors
                </p>
              </div>
              <button style={styles.watchNowBtn}>Watch Now</button>
            </div>

          </div>
        </CenteredContainer>
      </main>

      {/* Slide-over Bucket Drawer */}
      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: theme.colors.grey001,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.typography.bodyMD.fontFamily,
    color: theme.colors.charcoal,
  },
  mainFeedShell: {
    width: '100%',
    padding: `${theme.spacing[300]}px 0 ${theme.spacing[600]}px 0`,
  },
  feedColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[300],
  },
  liveOrderBanner: {
    backgroundColor: '#2B2A29',
    borderRadius: theme.radius[16],
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.colors.pureWhite,
    ...theme.shadows.cardShadow,
  },
  liveOrderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  truckIconLive: {
    fontSize: 24,
  },
  liveOrderHeading: {
    ...theme.typography.headingMD,
    color: theme.colors.pureWhite,
    margin: 0,
  },
  liveOrderSub: {
    ...theme.typography.bodySM,
    color: '#A0AEC0',
  },
  trackOrderBtn: {
    backgroundColor: theme.colors.brand,
    color: theme.colors.pureWhite,
    border: 'none',
    borderRadius: theme.radius[8],
    padding: '8px 16px',
    ...theme.typography.buttonSM,
    fontWeight: '700',
    cursor: 'pointer',
  },
  heroRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: theme.spacing[200],
  },
  heroSlideCard: {
    backgroundColor: theme.colors.brandTint,
    borderRadius: theme.radius[16],
    border: `1px solid ${theme.colors.brandTint}`,
    padding: theme.spacing[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 140,
    boxSizing: 'border-box',
  },
  heroSlideAltCard: {
    backgroundColor: '#FEF3C7',
    borderRadius: theme.radius[16],
    border: '1px solid #FDE68A',
    padding: theme.spacing[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 140,
    boxSizing: 'border-box',
  },
  heroWhatsAppCard: {
    backgroundColor: '#ECFDF5',
    borderRadius: theme.radius[16],
    border: '1px solid #A7F3D0',
    padding: theme.spacing[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 140,
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  heroTextSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[50],
  },
  heroTagline: {
    ...theme.typography.bodySM,
    color: theme.colors.bitumen,
  },
  heroHeading: {
    ...theme.typography.displayXL,
    fontSize: 26,
    lineHeight: '28px',
    color: theme.colors.bitumen,
    margin: 0,
  },
  heroHeadingAlt: {
    ...theme.typography.headingMD,
    fontSize: 16,
    lineHeight: '20px',
    color: theme.colors.bitumen,
    margin: 0,
  },
  heroSubtext: {
    ...theme.typography.bodySM,
    color: theme.colors.concrete,
  },
  heroSubtextAlt: {
    ...theme.typography.bodySM,
    fontSize: 11,
    lineHeight: '14px',
    color: theme.colors.bitumen,
    margin: 0,
  },
  heroLocationRow: {
    marginTop: theme.spacing[50],
    cursor: 'pointer',
  },
  heroLocationLink: {
    ...theme.typography.bodySM,
    fontSize: 11,
    color: theme.colors.brand,
    fontWeight: '600',
  },
  heroLocationLinkGreen: {
    ...theme.typography.bodySM,
    fontSize: 11,
    color: '#059669',
    fontWeight: '700',
  },
  urgencyBadge: {
    backgroundColor: theme.colors.brand,
    alignSelf: 'flex-start',
    padding: '2px 6px',
    borderRadius: theme.radius[8],
    marginBottom: 2,
  },
  urgencyBadgeText: {
    ...theme.typography.bodySM,
    fontSize: 9,
    color: theme.colors.pureWhite,
    fontWeight: '700',
  },
  whatsappBadge: {
    backgroundColor: '#059669',
    alignSelf: 'flex-start',
    padding: '2px 6px',
    borderRadius: theme.radius[8],
    marginBottom: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  whatsappBadgeText: {
    ...theme.typography.bodySM,
    fontSize: 9,
    color: theme.colors.pureWhite,
    fontWeight: '700',
  },
  whatsappMiniSvg: {
    width: 10,
    height: 10,
    objectFit: 'contain',
  },
  truckIconPlaceholder: {
    width: 72,
    height: 72,
    borderRadius: theme.radius[12],
    backgroundColor: theme.colors.pureWhite,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.fieldShadow,
    flexShrink: 0,
  },
  truckEmoji: {
    fontSize: 36,
  },
  clockIconPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: theme.colors.pureWhite,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.fieldShadow,
    flexShrink: 0,
  },
  clockEmoji: {
    fontSize: 30,
  },
  whatsappIconPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#25D366',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.fieldShadow,
    flexShrink: 0,
  },
  whatsappHeroSvg: {
    width: 32,
    height: 32,
    objectFit: 'contain',
  },
  sectionContainer: {
    marginBottom: theme.spacing[100],
  },
  sectionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[150],
  },
  sectionTitle: {
    ...theme.typography.headingMD,
    fontSize: 16,
    color: theme.colors.bitumen,
    margin: 0,
  },
  viewAllBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  },
  viewAllText: {
    ...theme.typography.bodySM,
    color: theme.colors.bitumen,
  },
  arrowIcon: {
    width: 12,
    height: 12,
    objectFit: 'contain',
  },
  arrowIcon14: {
    width: 14,
    height: 14,
    objectFit: 'contain',
  },
// Add or replace in the styles object of Home.js

categoriesGrid: {
  display: 'grid',
  // Scales tiles to a prominent 120px–136px desktop grid width
  gridTemplateColumns: 'repeat(auto-fill, minmax(124px, 1fr))',
  columnGap: `${theme.spacing[200]}px`, // 16px
  rowGap: `${theme.spacing[300]}px`,    // 24px
  width: '100%',
  alignItems: 'start',
},

categoryItemCard: {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  userSelect: 'none',
  textDecoration: 'none',
  transition: 'transform 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
},

categoryImgBox: {
  width: '100%',
  maxWidth: 120,
  height: 120,
  backgroundColor: '#F4F5F7',
  borderRadius: theme.radius[16],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${theme.spacing[100]}px`, // Reduced padding brings image closer to bounds
  boxSizing: 'border-box',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
},

categoryImg: {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  borderRadius: theme.radius[8],
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.06))',
  transition: 'transform 0.2s ease',
},

categoryFallbackBox: {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: theme.radius[12],
},

toteIcon28: {
  width: 40,
  height: 40,
  opacity: 0.35,
},

categoryItemName: {
  marginTop: 0, // Immediate proximity to the image card
  fontSize: 13.5,
  lineHeight: '17px',
  fontFamily: theme.typography.headingMD.fontFamily,
  fontWeight: '700',
  color: theme.colors.bitumen || '#2B2A29',
  textAlign: 'center',
  width: '100%',
  maxWidth: 120,
  // Completely untruncated (no ellipsis, no line clamps)
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'break-word',
  letterSpacing: '-0.15px',
},
  discountsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing[150],
  },
  discountCard: {
    borderRadius: theme.radius[12],
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing[150],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[100],
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  couponCardBg: {
    backgroundColor: theme.colors.brandTint,
    borderColor: theme.colors.brandTint,
  },
  referralCardBg: {
    backgroundColor: '#ECFDF5',
    borderColor: '#A7F3D0',
  },
  discountIconBadgeRed: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: theme.colors.brand,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  percentText: {
    ...theme.typography.headingMD,
    fontSize: 16,
    color: theme.colors.pureWhite,
    fontWeight: '700',
  },
  discountIconBadgeGreen: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: theme.colors.pureWhite,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  referralIconText: {
    fontSize: 16,
  },
  discountCardTextGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 5,
  },
  couponCodeTitle: {
    ...theme.typography.monoMD,
    fontSize: 13,
    color: theme.colors.brand,
    fontWeight: '700',
    marginBottom: 2,
  },
  referralTitle: {
    ...theme.typography.bodyLG,
    fontSize: 13,
    color: theme.colors.success,
    fontWeight: '700',
  },
  discountDesc: {
    ...theme.typography.bodySM,
    fontSize: 12,
    color: theme.colors.bitumen,
    lineHeight: '14px',
  },
  discountMinOrder: {
    ...theme.typography.bodySM,
    fontSize: 10,
    color: theme.colors.concrete,
    marginTop: 2,
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: theme.spacing[100],
  },
  productCard: {
    backgroundColor: theme.colors.pureWhite,
    borderRadius: theme.radius[12],
    border: `0.5px solid ${theme.colors.grey002}`,
    overflow: 'hidden',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
  },
  recentImage: {
    width: '100%',
    height: 86,
    backgroundColor: theme.colors.pureWhite,
    objectFit: 'contain',
    margin: '8px 0',
  },
  recentImageFallback: {
    width: '100%',
    height: 86,
    backgroundColor: theme.colors.pureWhite,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '8px 0',
  },
  toteIcon20: {
    width: 20,
    height: 20,
    opacity: 0.45,
  },
  productCardBody: {
    padding: `${theme.spacing[50] + 2}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  productName: {
    ...theme.typography.bodyLG,
    fontSize: 12,
    lineHeight: '14px',
    color: theme.colors.bitumen,
    height: 28,
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    margin: '2px 0',
  },
  productPrice: {
    ...theme.typography.monoLG,
    fontSize: 13,
    color: theme.colors.bitumen,
    fontWeight: '700',
  },
  productMrp: {
    ...theme.typography.monoLG,
    fontSize: 10,
    color: theme.colors.concrete,
    textDecoration: 'line-through',
  },
  brandsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: theme.spacing[100],
  },
  brandLogoCard: {
    height: 52,
    borderRadius: theme.radius[8],
    border: `0.5px solid ${theme.colors.grey002}`,
    backgroundColor: theme.colors.pureWhite,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  brandInnerBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
    boxSizing: 'border-box',
  },
  brandLogoText: {
    ...theme.typography.headingMD,
    fontSize: 10,
    textAlign: 'center',
    lineHeight: '12px',
    whiteSpace: 'pre-line',
  },
  testimonialBanner: {
    backgroundColor: theme.colors.brandTint,
    borderRadius: theme.radius[16],
    border: `1px solid ${theme.colors.brandTint}`,
    padding: theme.spacing[150],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[100],
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  playIconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.colors.brand,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  playIconText: {
    fontSize: 14,
    color: theme.colors.pureWhite,
    marginLeft: 2,
  },
  testimonialTextCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  testimonialTitle: {
    ...theme.typography.headingMD,
    fontSize: 13,
    color: theme.colors.bitumen,
    margin: 0,
  },
  testimonialSubtext: {
    ...theme.typography.bodySM,
    fontSize: 10,
    color: theme.colors.concrete,
    margin: 0,
  },
  watchNowBtn: {
    backgroundColor: theme.colors.brand,
    padding: '6px 14px',
    borderRadius: theme.radius[8],
    border: 'none',
    color: theme.colors.pureWhite,
    ...theme.typography.buttonSM,
    fontSize: 11,
    cursor: 'pointer',
    flexShrink: 0,
  },
  stateContainer: {
    padding: theme.spacing[300],
    textAlign: 'center',
    color: theme.colors.concrete,
    ...theme.typography.bodySM,
  },
  errorText: {
    color: theme.colors.error,
    ...theme.typography.bodySM,
    padding: theme.spacing[200],
    textAlign: 'center',
  },
};